import { useContext, useCallback } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { clearAllCaches, clearAuthCache, supabase, deleteUserVideos } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

export const useAuth = () => {
  const context = useContext(AuthContext);
  const navigate = useNavigate();
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  // Add a function to delete user account
  const deleteAccount = useCallback(async (): Promise<{ success: boolean; message: string }> => {
    try {
      // Get the current user ID
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return { 
          success: false, 
          message: 'No user found to delete' 
        };
      }
      
      // 1. Delete user's videos
      console.log('Deleting videos for user:', user.id);
      const deleteResult = await deleteUserVideos(user.id);
      
      if (!deleteResult.success) {
        console.warn('Warning: Video deletion may not have been complete:', deleteResult.message);
      }
      
      // 2. Delete the user's account
      console.log('Deleting user account:', user.id);
      const { error: deleteError } = await supabase.auth.admin.deleteUser(user.id);
      
      if (deleteError) {
        throw new Error(`Failed to delete account: ${deleteError.message}`);
      }
      
      // 3. Clear all caches and sign out
      clearAllCaches();
      await context.signOut();
      
      // 4. Redirect to home page
      navigate('/', { replace: true });
      
      return { 
        success: true, 
        message: 'Account deleted successfully' 
      };
    } catch (error) {
      console.error('Error deleting account:', error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Unknown error deleting account' 
      };
    }
  }, [context, navigate]);
  
  // Add a wrapper around signOut to clear caches when signing out
  const enhancedSignOut = useCallback(async () => {
    try {
      // Get the current user ID before signing out
      const { data: { user } } = await supabase.auth.getUser();
      
      // Clear auth session from local storage
      localStorage.removeItem('supabase.auth.token');
      localStorage.removeItem('supabase.auth.expires_at');
      localStorage.removeItem('supabase.auth.refresh_token');
      localStorage.removeItem('subscriptionCache');
      localStorage.removeItem('current_subscription_id');
      localStorage.removeItem('selectedPlan');
      
      // Clear all caches before signing out
      clearAllCaches();
      
      // Call the original signOut
      const result = await context.signOut();
      
      // If the user was signed out and we have their ID, delete their videos
      if (user) {
        try {
          console.log('Deleting videos for user after sign out:', user.id);
          await deleteUserVideos(user.id);
        } catch (videoError) {
          console.error('Error deleting user videos during sign out:', videoError);
          // Don't throw error here, as we still want to complete the sign out process
        }
      }
      
      return result;
    } catch (error) {
      console.error('Error in signOut:', error);
      throw error;
    }
  }, [context]);
  
  // Add a function to handle refresh token errors
  const handleRefreshTokenError = useCallback(async () => {
    console.log('Handling refresh token error - clearing session and redirecting to login');

    // Define public routes that don't require authentication
    const publicRoutes = ['/login', '/signup', '/home', '/', '/features', '/about', '/terms', '/privacy', '/pricing'];
    
    // Check if current route is public
    const isPublicRoute = publicRoutes.some(route => 
      window.location.pathname === route || 
      window.location.pathname.startsWith(route + '/')
    );
    
    // If we're already on a public route, just clear the cache but don't redirect
    if (isPublicRoute) {
      console.log('Already on public route, just clearing auth cache');
      clearAuthCache();
      return;
    }
    
    try {
      // Get the current user ID before signing out
      const { data: { user } } = await supabase.auth.getUser();
      
      // Clear all caches and local storage
      clearAllCaches();
      
      // Force clear the session in Supabase
      await supabase.auth.signOut();
      
      // If we have the user ID, delete their videos
      if (user) {
        try {
          console.log('Deleting videos for user after refresh token error:', user.id);
          await deleteUserVideos(user.id);
        } catch (videoError) {
          console.error('Error deleting user videos during refresh token error handling:', videoError);
          // Don't throw error here, as we still want to complete the redirect
        }
      }
      
      // Return to login page
      window.location.href = '/login';
    } catch (error) {
      console.error('Error handling refresh token error:', error);
      // Force redirect to login even if there's an error
      window.location.href = '/login';
    }
  }, []);
  
  return {
    ...context,
    signOut: enhancedSignOut,
    handleRefreshTokenError,
    deleteAccount
  };
};